<template>
  <DossierDocumentCard
    title="Registro no Conselho Municipal de Assistência Social (CMAS)"
    :status-options="statusOptions"
    :multiple-status="true"
    :status-requires-validity="['possui_vigente', 'possui_provisorio']"
    :status-requires-upload="['possui_vigente', 'possui_provisorio']"
    :explainer-text="explainerText"
    :mode="mode"
    :not-applicable="isNotApplicable"
    :start-collapsed="startCollapsed"
    v-model="formData"
    save-endpoint="/rest-home/dossier/welfare-council"
    upload-endpoint="/rest-home/dossier/welfare-council/upload"
    @saved="onSaved"
    @error="onError"
    v-show="mode == 'view' ? formData.file_url : true"
  >
    <template #fields-before>
      <!-- ── Pergunta condicional ── -->
      <!-- Exibida SEMPRE, independente do modo, pois define se o módulo se aplica -->
      <div class="dossier-conditional__question">
        <label class="dossier-conditional__question-label">
          <i class="fas fa-question-circle mr-1"></i>
          A instituição é filantrópica, pública ou sem fins lucrativos?
        </label>

        <div class="dossier-conditional__options">
          <label class="dossier-conditional__option">
            <input
              type="radio"
              value="sim"
              v-model="formData.is_applicable_answer"
              :disabled="isViewMode"
              class="dossier-conditional__radio"
            />
            <span>Sim</span>
          </label>
          <label class="dossier-conditional__option">
            <input
              type="radio"
              value="nao"
              v-model="formData.is_applicable_answer"
              :disabled="isViewMode"
              class="dossier-conditional__radio"
            />
            <span>Não — não se aplica a esta instituição</span>
          </label>
        </div>

        <!-- Explicação quando não se aplica -->
        <div
          v-if="formData.is_applicable_answer === 'nao'"
          class="dossier-conditional__na-message"
        >
          <i class="fas fa-info-circle mr-1"></i>
          O registro no CMAS é exigido apenas para instituições filantrópicas,
          públicas ou sem fins lucrativos. Este módulo não gera pendência para
          instituições privadas com fins lucrativos.
        </div>
      </div>

      <!-- Campos do registro (exibidos apenas quando aplicável) -->
      <template v-if="formData.is_applicable_answer === 'sim'">
        <div class="dossier-module__fields">
          <div class="dossier-module__field-group">
            <label class="dossier-module__label">Nome do CMAS</label>
            <v-text-field
              v-if="!isViewMode"
              v-model="formData.council_name"
              dense
              outlined
              hide-details
              placeholder="Ex: CMAS de São Paulo"
              class="dossier-module__input"
            />
            <span v-else class="dossier-module__view-value">{{
              formData.council_name || "—"
            }}</span>
          </div>

          <div
            class="dossier-module__field-group dossier-module__field-group--small"
          >
            <label class="dossier-module__label">Número do registro</label>
            <v-text-field
              v-if="!isViewMode"
              v-model="formData.registration_number"
              dense
              outlined
              hide-details
              placeholder="Número ou protocolo"
              class="dossier-module__input"
            />
            <span v-else class="dossier-module__view-value">{{
              formData.registration_number || "—"
            }}</span>
          </div>
        </div>
      </template>
    </template>
  </DossierDocumentCard>
</template>

<script>
import DossierDocumentCard from "../dossier/DossierDocumentCard.vue";

const EXPLAINER_TEXT = `
  O registro no Conselho Municipal de Assistência Social (CMAS) é obrigatório
  para ILPIs filantrópicas, públicas ou sem fins lucrativos que recebem
  financiamento público ou têm vínculo com a rede socioassistencial.<br><br>
  Para instituições privadas com fins lucrativos, este documento não é exigido
  e o módulo não gera pendência.<br><br>
  O Sistema Clin não valida o conteúdo do documento, apenas registra sua
  existência para fins de organização e apresentação à fiscalização.
`;

export default {
  name: "DossierWelfareCouncil",
  components: { DossierDocumentCard },
  props: {
    mode: {
      type: String,
      default: "edit",
      validator: (v) => ["edit", "view"].includes(v),
    },
    initialData: { type: Object, default: () => ({}) },
    startCollapsed: { type: Boolean, default: false },
  },
  data() {
    return {
      explainerText: EXPLAINER_TEXT,
      statusOptions: [
        { value: "possui_vigente", label: "Possui registro vigente" },
        { value: "possui_provisorio", label: "Possui registro provisório" },
        {
          value: "em_elaboracao",
          label: "Em processo de registro ou renovação",
        },
        { value: "nao_possui", label: "Não possui (pendente)" },
      ],
      formData: {
        // Resposta da pergunta condicional: 'sim' | 'nao' | null (não respondido)
        is_applicable_answer: null,
        council_name: "",
        registration_number: "",
        status: [],
        validity_date: null,
        file_url: null,
        file_name: null,
        observations: "",
        ...this.flattenInitialData(),
      },
    };
  },
  computed: {
    isViewMode() {
      return this.mode === "view";
    },

    // Passa notApplicable=true para o DossierDocumentCard
    // quando a instituição respondeu que não se aplica
    isNotApplicable() {
      return this.formData.is_applicable_answer === "nao";
    },
  },
  watch: {
    initialData: {
      deep: true,
      handler() {
        Object.assign(this.formData, this.flattenInitialData());
      },
    },
  },
  methods: {
    flattenInitialData() {
      const d = this.initialData || {};
      return {
        is_applicable_answer: d.is_applicable_answer || null,
        council_name: d.council_name || "",
        registration_number: d.registration_number || "",
        status: d.status || [],
        validity_date: d.validity_date || null,
        file_url: d.file_url || null,
        file_name: d.file_name || null,
        observations: d.observations || "",
      };
    },
    onSaved(data) {
      this.$emit("saved", { module: "welfare_council", data });
    },
    onError(errors) {
      this.$emit("error", { module: "welfare_council", errors });
    },
  },
};
</script>